import { api, request, WP_USER_API_URL, JWT_AUTH_URL, API_URL, PROJECT_URL, getAuthToken } from './axiosInstance';
import axios from 'axios';
// Register user
// Register a new customer.
//
// Previously POSTed to wc/v3/customers using the shared storefront token. That only
// worked because the account was a full Administrator — `create_users` is an
// administrator-only capability — so registration silently depended on the
// over-privileged account and broke when it was scoped down to `headless_reader`.
//
// Now uses POST custom/v1/register, which is public (a registering visitor has no
// account yet), forces the role to `customer` server-side regardless of what the
// client sends, enforces a password minimum, and is rate limited per IP.
export async function registerUser(userInfo) {
    try {
        return await axios.post(
            `${PROJECT_URL}wp-json/custom/v1/register`,
            {
                email: userInfo.email,
                username: userInfo.username,
                name: userInfo.name,
                password: userInfo.password,
                // `role` is deliberately not sent. The server ignores it and always
                // assigns `customer`; sending it would imply the client can choose.
            },
            { headers: { 'Content-Type': 'application/json' } }
        );
    } catch (error) {
        console.error('Error registering user:', {
            response: error.response?.data,
            status: error.response?.status,
        });
        // Surface the server's message — "that username is taken" or "password must be
        // at least 8 characters" is actionable, "Failed to register user" is not.
        throw new Error(error.response?.data?.message || 'Failed to register user');
    }
}

// Login user
export async function loginUser(userInfo) {
    try {
        const response = await axios.post(JWT_AUTH_URL, userInfo);
        const token = response.data.token;
        localStorage.setItem('auth_token', token);
        return response.data;
    } catch (error) {
        console.error('Error logging in user:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to login user');
    }
}
// Get user data
export async function getUserData(token) {
    try {
        const response = await axios.get(`${PROJECT_URL}wp-json/wp/v2/users/me`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch user data');
    }
}

// Get customer details
// Read the signed-in customer's own profile via custom/v1/me/profile.
//
// Previously GET wc/v3/customers/{id}, which requires `list_users` — an admin
// capability the `customer` role lacks. The endpoint below is scoped to the
// authenticated user; the customerId and token arguments are kept only so existing
// callers (Checkout.jsx, Profile.jsx) need no change, and are ignored — the server
// determines whose profile this is from the bearer token.
export async function getCustomerById(customerId, token) {
    const bearer = token || await getAuthToken();
    try {
        const response = await axios.get(`${PROJECT_URL}wp-json/custom/v1/me/profile`, {
            headers: { Authorization: `Bearer ${bearer}` },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching customer:', {
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch customer');
    }
}

// Update the signed-in customer's own profile. Same note as above: the server
// derives identity from the token, so one customer cannot write another's record.
export async function updateWooCustomer(customerId, token, payload) {
    const bearer = token || await getAuthToken();
    try {
        const response = await axios.put(
            `${PROJECT_URL}wp-json/custom/v1/me/profile`,
            payload,
            { headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' } }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating customer:', {
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update customer');
    }
}


// Get all user data
export async function getAllUserData(page = 1, per_page = 10) {
    try {
        const response = await request('GET', WP_USER_API_URL, {}, { page, per_page });
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching all user data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch all user data');
    }
}

// Update customer data
export async function updateCustomerData(userId, data) {
    try {
        return await request('PUT', `${WP_USER_API_URL}/${userId}`, data);
    } catch (error) {
        console.error('Error updating customer data:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update customer data');
    }
}

// Update customer role
export async function updateCustomerRole(userId, role) {
    try {
        return await request('PUT', `${WP_USER_API_URL}/${userId}`, { roles: [role] });
    } catch (error) {
        console.error('Error updating customer role:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to update customer role');
    }
}

// Delete customer by ID
export async function deleteCustomerById(customerId) {
    try {
        if (!customerId) {
            throw new Error('Customer ID is required');
        }
        return await request('DELETE', `${API_URL}/customers/${customerId}`, {}, { force: true });
    } catch (error) {
        console.error('Error deleting customer:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to delete customer');
    }
}

// Get all customers
export async function getAllCustomers() {
    try {
        const response = await request('GET', `${API_URL}/customers`);
        return {
            data: response.data,
            total: response.headers?.['x-wp-total'] || response.length,
            totalPages: response.headers?.['x-wp-totalpages'] || 1,
        };
    } catch (error) {
        console.error('Error fetching customers:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error(error.response?.data?.message || 'Failed to fetch customers');
    }
}
